import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader, DashboardCard, FilterBar, StatusBadge,
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@zafroi/ui-kit';
import { useQuotes } from '@/hooks/useCommercial';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { quoteStatusVariants } from '@/config/commercial-status';
import { formatMoney, shortDate } from '@/lib/format';

export function QuotesPage() {
  const navigate = useNavigate();
  const q = useQuotes();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');
  const rows = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((x) => x.quoteNumber.toLowerCase().includes(s) || (nameMap.get(x.customerId) ?? '').toLowerCase().includes(s));
  }, [rows, search, nameMap]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Commercial" title="Quotes" description="Priced quotes — send, accept, and convert to contracts." />
      <DashboardCard flushBody>
        <div className="border-b border-border p-3">
          <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search quotes…" />
        </div>
        <QueryStateGuard data={filtered} isLoading={q.isLoading} isError={q.isError} emptyTitle="No quotes yet">
          {(data) => (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Number</TableHead><TableHead>Customer</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
                <TableHead>Valid until</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((x) => (
                  <TableRow key={x.id} className="cursor-pointer" onClick={() => navigate(`/quotes/${x.id}`)}>
                    <TableCell className="font-mono text-[12px]">{x.quoteNumber}</TableCell>
                    <TableCell className="font-medium">{nameMap.get(x.customerId) ?? x.customerId.slice(0, 8)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(x.total, x.currency)}</TableCell>
                    <TableCell><StatusBadge status={x.status} variants={quoteStatusVariants} /></TableCell>
                    <TableCell className="text-muted-foreground">{x.validUntil ? shortDate(x.validUntil) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
